import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { EyeOff } from 'lucide-react';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/pt-br';
import ChapterComments from '@/components/capitulo/ChapterComments';

dayjs.extend(relativeTime);
dayjs.locale('pt-br');

/** Detecta [SPOILER] ou <spoiler> no texto (início ou fim). Convenção só no front, sem backend. */
function parseSpoiler(text: string): { isSpoiler: boolean; displayText: string } {
    if (!text || typeof text !== 'string') return { isSpoiler: false, displayText: text || '' };
    const t = text.trim();
    const startMatch = t.match(/^(\[SPOILER\]|<\s*spoiler\s*>)\s*/i);
    const endMatch = t.match(/\s*(\[\/?SPOILER\]|<\s*\/\s*spoiler\s*>)$/i);
    let displayText = t;
    if (startMatch) displayText = displayText.slice(startMatch[0].length).trim();
    if (endMatch) displayText = displayText.slice(0, -endMatch[0].length).trim();
    const isSpoiler = !!startMatch || !!endMatch;
    return { isSpoiler, displayText: displayText || '(spoiler)' };
}

/** Envolve conteúdo (texto + img) em blur; clique revela. */
function SpoilerOverlay({ children, className = '' }: { children: React.ReactNode; className?: string }) {
    const [revealed, setRevealed] = useState(false);
    if (revealed) return <div className={className}>{children}</div>;
    return (
        <div className={`relative ${className}`}>
            <div className="select-none pointer-events-none blur-md saturate-0 contrast-50 scale-[1.02] min-h-[2rem]" aria-hidden>
                {children}
            </div>
            <button
                type="button"
                onClick={() => setRevealed(true)}
                className="absolute inset-0 flex items-center justify-center gap-2 bg-black/60 hover:bg-black/70 transition-colors rounded-lg text-white/90 text-sm cursor-pointer z-20"
            >
                <EyeOff size={18} />
                Spoiler — clique para revelar
            </button>
        </div>
    );
}

interface CommunityCommentItemProps {
    comment: any;
    onRefresh: () => void;
    onReply: (parentId: number, text: string) => Promise<void>;
    onXpGain: (xp: number, message: string) => void;
    depth?: number;
}

export default function ComunidadePage() {
    const [searchParams] = useSearchParams();
    const pageFromUrl = Number(searchParams.get('pagina')) || 1;

    const [currentPage, setCurrentPage] = useState(pageFromUrl);
    const [totalPages, setTotalPages] = useState(1);
    const [feedTotalItems, setFeedTotalItems] = useState<number | null>(null);

    useEffect(() => { setCurrentPage(pageFromUrl); }, [pageFromUrl]);

    return (
        <div className="min-h-screen bg-black pb-12 pt-4 lg:pt-36">
            <div className="mx-auto max-w-4xl px-4 md:px-8">
                <div className="mb-12 space-y-6">
                    <div className="flex items-center gap-4">
                        <div className="w-1.5 h-10 bg-orange-600 rounded-full shadow-[0_0_15px_rgba(234,88,12,0.4)]" />
                        <div>
                            <h1 className="text-2xl md:text-4xl font-black tracking-tighter uppercase italic">Comunidade</h1>
                            <p className="text-[10px] font-black uppercase tracking-[0.3em] text-gray-500 mt-1">Compartilhe e descubra opiniões</p>
                        </div>
                    </div>

                </div>

            </div>
            <section className="mx-auto max-w-7xl px-4 pb-32 md:px-8">
                <ChapterComments
                    comunidade={true}
                    pagina={currentPage}
                    totalPages={totalPages}
                    onPaginationMeta={meta => {
                        setTotalPages(meta.totalPages);
                        setFeedTotalItems(meta.totalItems);
                    }}
                />
            </section>
        </div>
    );
}
